/**
 * Credentialed Dentiefy API client.
 *
 * The HttpOnly server cookie is the authentication credential. The CSRF token
 * and current session projection live only in this module's memory.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let csrfToken = null;
let sessionSnapshot = null;

export class ApiError extends Error {
  constructor(status, code, message, payload = {}) {
    super(message || code || 'request_failed');
    this.name = 'ApiError';
    this.status = status;
    this.code = code || 'request_failed';
    this.correlationId = payload.correlation_id;
    this.payload = payload;
    Object.assign(this, payload);
  }
}

function apiUrl(path) {
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function dispatchAuthRequired(error) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  const EventConstructor = typeof CustomEvent === 'function' ? CustomEvent : Event;
  window.dispatchEvent(new EventConstructor('dentiefy-auth-required', {
    detail: {
      code: error.code,
      status: error.status,
    },
  }));
}

function dispatchApiFeedback(error, { method, authenticated }) {
  if (
    !authenticated ||
    error.status === 401 ||
    typeof window === 'undefined' ||
    typeof window.dispatchEvent !== 'function'
  ) return;
  const EventConstructor = typeof CustomEvent === 'function' ? CustomEvent : Event;
  window.dispatchEvent(new EventConstructor('dentiefy-api-feedback', {
    detail: {
      code: error.code,
      status: error.status,
      correlationId: error.correlationId,
      retryable: method === 'GET',
    },
  }));
}

function cookieExpiry(name, domain = '') {
  const domainAttribute = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttribute}; SameSite=Lax`;
}

export function clearLegacySessionCookie() {
  if (typeof document === 'undefined') return;
  cookieExpiry('dentiefy-session');

  const hostname = window.location.hostname.toLowerCase();
  if (hostname.endsWith('.localhost')) cookieExpiry('dentiefy-session', '.localhost');
  const labels = hostname.split('.');
  if (labels.length > 1 && hostname !== 'localhost') {
    cookieExpiry('dentiefy-session', `.${labels.slice(-2).join('.')}`);
  }
}

export function clearInMemorySession({ notify = false, error } = {}) {
  csrfToken = null;
  sessionSnapshot = null;
  if (notify) {
    dispatchAuthRequired(error || new ApiError(
      401,
      'authentication_required',
      'Authentication is required.'
    ));
  }
}

async function readPayload(response) {
  const contentType = response.headers?.get?.('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => ({}));
  }
  const text = await response.text().catch(() => '');
  return text ? { message: text } : {};
}

function responseFacade(payload, status, ok) {
  return Object.freeze({
    ok,
    status,
    json: async () => payload,
    text: async () => JSON.stringify(payload),
  });
}

export async function apiRequest(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const authenticated = options.authenticated === true;
  const shouldDispatchFeedback = options.feedback !== false;
  const mutation = MUTATING_METHODS.has(method);

  if (authenticated && mutation && options.csrf !== false && !csrfToken) {
    const bootstrapped = await bootstrapSession();
    if (!bootstrapped) {
      const error = new ApiError(401, 'authentication_required', 'Authentication is required.');
      dispatchAuthRequired(error);
      throw error;
    }
  }

  const headers = { ...(options.headers || {}) };
  let body = options.body;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && body !== null && !isFormData && typeof body !== 'string') {
    body = JSON.stringify(body);
    if (!Object.keys(headers).some((name) => name.toLowerCase() === 'content-type')) {
      headers['Content-Type'] = 'application/json';
    }
  }
  if (authenticated && mutation && options.csrf !== false) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  let response;
  try {
    const requestOptions = { ...options };
    delete requestOptions.authenticated;
    delete requestOptions.csrf;
    delete requestOptions.feedback;
    response = await fetch(apiUrl(path), {
      ...requestOptions,
      method,
      headers,
      body,
      credentials: 'include',
    });
  } catch (cause) {
    const error = new ApiError(0, 'network_error', 'Unable to reach the service.', { cause });
    if (shouldDispatchFeedback) {
      dispatchApiFeedback(error, { method, authenticated });
    }
    throw error;
  }

  const payload = await readPayload(response);
  if (!response.ok) {
    const error = new ApiError(
      response.status,
      payload.error || `http_${response.status}`,
      payload.message || 'The request could not be completed.',
      payload
    );
    if (authenticated && response.status === 401) {
      clearInMemorySession({ notify: true, error });
    }
    if (shouldDispatchFeedback) {
      dispatchApiFeedback(error, { method, authenticated });
    }
    throw error;
  }
  return payload;
}

/**
 * Response-compatible adapter for callers that still consume `ok/status/json`.
 * It retains apiRequest's credential, CSRF and safe-error behavior.
 */
export async function apiFetch(path, options = {}) {
  try {
    const payload = await apiRequest(path, options);
    return responseFacade(payload, 200, true);
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;
    return responseFacade({
      error: error.code,
      message: error.message,
      correlation_id: error.correlationId,
    }, error.status, false);
  }
}

function normalizeSession(payload) {
  if (!payload?.user?.id || !payload?.user?.role || !payload?.csrf_token) {
    throw new ApiError(
      503,
      'security_service_unavailable',
      'The security service is temporarily unavailable.'
    );
  }
  const name = typeof payload.user.name === 'string'
    ? payload.user.name
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : payload.user.name;

  return Object.freeze({
    user: Object.freeze({ ...payload.user, name }),
    capabilities: Object.freeze(Array.isArray(payload.capabilities) ? [...payload.capabilities] : []),
    absolute_expires_at: payload.absolute_expires_at || null,
    is_simulation: payload.is_simulation === true,
  });
}

export async function bootstrapSession({ required = false } = {}) {
  try {
    const payload = await apiRequest('/api/auth/session/bootstrap');
    if (payload?.authenticated === false) {
      clearLegacySessionCookie();
      clearInMemorySession();
      if (!required) return null;
      throw new ApiError(401, 'authentication_required', 'Authentication is required.');
    }
    const session = normalizeSession(payload);
    csrfToken = payload.csrf_token;
    sessionSnapshot = session;
    clearLegacySessionCookie();
    return session;
  } catch (error) {
    clearLegacySessionCookie();
    if (error instanceof ApiError && error.status === 401) {
      clearInMemorySession();
      if (!required) return null;
    }
    throw error;
  }
}

export async function completeAuthentication() {
  return bootstrapSession({ required: true });
}

export async function refreshAuthenticatedSession() {
  return bootstrapSession({ required: true });
}

export function getSessionSnapshot() {
  return sessionSnapshot;
}

export const __apiClientTesting = Object.freeze({
  getCsrfToken: () => csrfToken,
  reset() {
    csrfToken = null;
    sessionSnapshot = null;
  },
});
